import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  HeaderAccentStyleKey,
  SettingsContext,
  SettingsContextType,
  ThemeKey,
} from './SettingsContext';
import { SxProps, Theme } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { ReemaThemeV2 } from '../reema-theme-V2';
import { HEADER_ACCENT_STYLES } from '../header-accent-styles';

interface SettingsProviderProps {
  children: React.ReactNode;
}

interface CustomizationSettings {
  themeKey: ThemeKey;
  headerAccentStyleKey: HeaderAccentStyleKey;
  isHeaderAccentAnimationEnabled: boolean;
}

const defaultCustomizationSettings: CustomizationSettings = {
  themeKey: 'LIGHT_MODE',
  headerAccentStyleKey: 'none',
  isHeaderAccentAnimationEnabled: false,
};

export const SettingsProvider = ({ children }: SettingsProviderProps) => {
  const [currentThemeKey, setCurrentThemeKey] = useState<ThemeKey | null>(null);
  const [currentHeaderAccentKey, setCurrentHeaderAccentKey] =
    useState<HeaderAccentStyleKey | null>(null);
  const [
    isHeaderAccentAnimationEnabledState,
    setIsHeaderAccentAnimationEnabledState,
  ] = useState<boolean | null>(null);

  const saveSettings = useCallback((settings: CustomizationSettings) => {
    try {
      localStorage.setItem('customizationSettings', JSON.stringify(settings));
    } catch (error) {
      console.error('Failed to save settings to localStorage:', error);
    }
  }, []);

  useEffect(() => {
    try {
      const savedSettings = localStorage.getItem('customizationSettings');
      let loadedSettings: CustomizationSettings;

      if (savedSettings === null) {
        loadedSettings = defaultCustomizationSettings;
        saveSettings(defaultCustomizationSettings);
      } else {
        loadedSettings = JSON.parse(savedSettings) as CustomizationSettings;

        loadedSettings.themeKey =
          (Object.keys(ReemaThemeV2).includes(loadedSettings.themeKey) &&
            loadedSettings.themeKey) ||
          defaultCustomizationSettings.themeKey;

        loadedSettings.headerAccentStyleKey =
          (HEADER_ACCENT_STYLES[loadedSettings.headerAccentStyleKey] &&
            loadedSettings.headerAccentStyleKey) ||
          defaultCustomizationSettings.headerAccentStyleKey;

        loadedSettings.isHeaderAccentAnimationEnabled =
          (typeof loadedSettings.isHeaderAccentAnimationEnabled === 'boolean' &&
            loadedSettings.isHeaderAccentAnimationEnabled) ||
          defaultCustomizationSettings.isHeaderAccentAnimationEnabled;
      }

      setCurrentThemeKey(loadedSettings.themeKey);
      setCurrentHeaderAccentKey(loadedSettings.headerAccentStyleKey);
      setIsHeaderAccentAnimationEnabledState(
        loadedSettings.isHeaderAccentAnimationEnabled
      );
    } catch (error) {
      console.error(
        'Failed to load or parse settings from localStorage:',
        error
      );
      setCurrentThemeKey(defaultCustomizationSettings.themeKey);
      setCurrentHeaderAccentKey(
        defaultCustomizationSettings.headerAccentStyleKey
      );
      setIsHeaderAccentAnimationEnabledState(
        defaultCustomizationSettings.isHeaderAccentAnimationEnabled
      );
      saveSettings(defaultCustomizationSettings);
    }
  }, [saveSettings]);

  const actualThemeKey =
    currentThemeKey ?? defaultCustomizationSettings.themeKey;
  const actualHeaderAccentKey =
    currentHeaderAccentKey ?? defaultCustomizationSettings.headerAccentStyleKey;
  const actualIsHeaderAccentAnimationEnabled =
    isHeaderAccentAnimationEnabledState ??
    defaultCustomizationSettings.isHeaderAccentAnimationEnabled;

  const setThemeKey = useCallback(
    (newThemeKey: ThemeKey) => {
      if (Object.keys(ReemaThemeV2).includes(newThemeKey)) {
        setCurrentThemeKey(newThemeKey);
        saveSettings({
          themeKey: newThemeKey,
          headerAccentStyleKey: actualHeaderAccentKey,
          isHeaderAccentAnimationEnabled: actualIsHeaderAccentAnimationEnabled,
        });
      } else {
        console.warn(`Attempted to set unknown theme key: ${newThemeKey}`);
      }
    },
    [saveSettings, actualHeaderAccentKey, actualIsHeaderAccentAnimationEnabled]
  );

  const setHeaderAccentStyleKey = useCallback(
    (newHeaderAccentStyleKey: HeaderAccentStyleKey) => {
      setCurrentHeaderAccentKey(newHeaderAccentStyleKey);
      saveSettings({
        themeKey: actualThemeKey,
        headerAccentStyleKey: newHeaderAccentStyleKey,
        isHeaderAccentAnimationEnabled: actualIsHeaderAccentAnimationEnabled,
      });
    },
    [saveSettings, actualThemeKey, actualIsHeaderAccentAnimationEnabled]
  );

  const setIsHeaderAccentAnimationEnabled = useCallback(
    (enabled: boolean) => {
      setIsHeaderAccentAnimationEnabledState(enabled);
      saveSettings({
        themeKey: actualThemeKey,
        headerAccentStyleKey: actualHeaderAccentKey,
        isHeaderAccentAnimationEnabled: enabled,
      });
    },
    [saveSettings, actualThemeKey, actualHeaderAccentKey]
  );

  const muiTheme = useMemo(() => {
    if (actualThemeKey) {
      return ReemaThemeV2[actualThemeKey];
    }
    return ReemaThemeV2[defaultCustomizationSettings.themeKey];
  }, [actualThemeKey]);

  const currentHeaderAccentStyle: SxProps<Theme> = useMemo(() => {
    const baseStyle = HEADER_ACCENT_STYLES[actualHeaderAccentKey];
    if (
      actualIsHeaderAccentAnimationEnabled &&
      baseStyle !== HEADER_ACCENT_STYLES.none
    ) {
      return {
        ...baseStyle,
        ...HEADER_ACCENT_STYLES.animated,
      } as SxProps<Theme>;
    }
    return baseStyle;
  }, [actualHeaderAccentKey, actualIsHeaderAccentAnimationEnabled]);

  const contextValue: SettingsContextType = useMemo(
    () => ({
      theme: muiTheme,
      themeKey: actualThemeKey,
      setThemeKey,
      currentHeaderAccentStyle,
      headerAccentStyleKey: actualHeaderAccentKey,
      isHeaderAccentAnimationEnabled: actualIsHeaderAccentAnimationEnabled,
      setHeaderAccentStyleKey,
      setIsHeaderAccentAnimationEnabled,
    }),
    [
      muiTheme,
      actualThemeKey,
      setThemeKey,
      currentHeaderAccentStyle,
      actualHeaderAccentKey,
      actualIsHeaderAccentAnimationEnabled,
      setHeaderAccentStyleKey,
      setIsHeaderAccentAnimationEnabled,
    ]
  );

  if (
    currentThemeKey === null ||
    currentHeaderAccentKey === null ||
    isHeaderAccentAnimationEnabledState === null
  ) {
    return null;
  }

  return (
    <SettingsContext.Provider value={contextValue}>
      <ThemeProvider theme={muiTheme}>{children}</ThemeProvider>
    </SettingsContext.Provider>
  );
};
